import authStoreGetters from '../authentication/store/getters'
import { gql } from '@apollo/client/core'
import {
  AddReviewDto,
  AddSiteReviewDto,
  ArcClaimDto,
  ArcDto,
  ArcEligibleGroupResultDto,
  ArcFollowerStatsDto,
  ArcGetEligibleArgs,
  ArcPagedResultDto,
  ArcReviewDto,
  ArcSite,
  ArcWebStatsDto,
  AvailableDownloadCodeDto,
  BookDto,
  bsConstants,
  GetActiveArcDatesArgs,
  getArcSites,
  getArcSitesFromFlags,
  HasIndex,
  IdDto,
  isValidReviewLink,
  KvpDto,
  ReportPirateArgs,
  UsedDownloadCodeDto,
  validStr
} from 'booksprout'
import { ArcWebStatsField, FindArcFilters } from './arcTypes'
import { arcFragments } from './fragments'
import { LocalStorage } from 'quasar'
import { countSiteFlags, getArcResult } from './arcHelpers'
import { useApollo } from '../apollo/apolloModule'
import { useAxios } from '../axios/axiosModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Reviews, stats and everything a reviewer browses by: adding/translating/deleting a review, the
 * eligible-arc queries behind Find Review Copies, the web and user stat blocks, download-code counts,
 * view/impression tracking, the pirate report, and the pure site/language/filter helpers the arc UI
 * leans on.
 *
 * One of three services that replace `ArcService` (arc.service.reviews-stats.spec.ts is this lane).
 *
 * `getArcSites`, `getArcSitesFromFlags` and `isValidReviewLink` are re-exported straight from
 * `booksprout`. The class declared methods of the same name whose whole body was a call to the import —
 * a pass-through that only worked because `this.` disambiguated them.
 */

const maxEmailFileSize = 7.1 * 1024 * 1024 //10mb but when it's base64'd it'll grow by about 39%

const defaultWebStatsFields: ArcWebStatsField[] = [
  'arcReviewCount',
  'arcReviewUserCount',
  'arcLiveCount',
  'authorCount',
  'arcActiveCount',
  'topArc'
]

const reportPirate = (args: ReportPirateArgs, file: File): Promise<any> => {
  // Construct our form data
  const bodyFormData = new FormData()
  bodyFormData.append('bookId', args.bookId.toString())
  bodyFormData.append('fileName', file.name)
  bodyFormData.append('link', args.link)
  bodyFormData.append('uploadFile', file)

  // Post it to the AuthController on the server
  return useAxios().axios()({
    method: 'post',
    url: crud.getApiControllerUrl('report-pirate'),
    headers: {
      Authorization: `Bearer ${authStoreGetters.authToken}`,
      'Content-Type': 'multipart/form-data',
      'CSRF-Token': authStoreGetters.csrfToken
    },
    data: bodyFormData
  })
}

const getReportedPirateCount = (): Promise<number> => {
  return useApollo().query({
    query: gql`
      query GetReportedPirateCount {
        reportedPirateCount
      }
    `
  }).then((r: any) => {
    return r
  })
}

const addReview = (args: AddReviewDto): Promise<ArcReviewDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation AddReview (
        $claimId: Int!,
        $disclaimer: String!,
        $privateFeedback: String!,
        $rating: Int!,
        $review: String!,
        $title: String!
      ) {
        addReview (
          claimId: $claimId,
          disclaimer: $disclaimer,
          privateFeedback: $privateFeedback,
          rating: $rating,
          review: $review,
          title: $title
        ) {
          id
        }
      }
    `
  })
}

const translateReview = (reviewId: number): Promise<ArcReviewDto> => {
  return useApollo().mutate({
    variables: {
      id: reviewId
    },
    mutation: gql`
      mutation TranslateReview (
        $id: Int!
      ) {
        translateReview (id: $id) {
          id
          reviewLanguage
          translatedReview
        }
      }
    `
  })
}

const deleteReview = (reviewId: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id: reviewId
    },
    mutation: gql`
      mutation DeleteReview (
        $id: Int!
      ) {
        deleteReview (id: $id)
      }
    `
  })
}

const addSiteReview = (args: AddSiteReviewDto): Promise<IdDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation AddSiteReview (
        $link: String,
        $reviewId: Int!,
        $site: Int,
        $status: Int,
        $userId: Int!,
        $statusReason: String
      ) {
        addSiteReview (
          link: $link,
          reviewId: $reviewId,
          site: $site,
          status: $status,
          userId: $userId,
          statusReason: $statusReason
        ) {
          id
        }
      }
    `
  })
}

const getArcSitesFromFlagsExcludingCountrySpecificAudible = (flags: number): ArcSite[] => {
  const arcSites = getArcSitesFromFlags(flags)
  const { AUDIBLE_US, AUDIBLE_UK } = bsConstants.ARCS.REVIEWS.FLAGS

  return arcSites.filter(flag => {
    return ![AUDIBLE_US, AUDIBLE_UK].includes(flag.site)
  })
}

const getEligibleGroupedCount = (params?: ArcGetEligibleArgs): Promise<ArcEligibleGroupResultDto> => {
  return useApollo().query<ArcEligibleGroupResultDto>({
    variables: {
      ...params
    },
    query: gql`
      query GetGroupedEligibleArcsCount (
        $needle: String,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $penNameId: Int
        $hasComeViaLink: Boolean,
        $arcTeamId: Int,
        $activeOnly: Boolean,
        $pastOnly: Boolean,
        $arcType: Int,
        $sites: Int,
        $minLength: Int,
        $maxLength: Int,
        $audiences: [Int!],
        $languages: [Int!],
        $canReviewInDays: Int,
        $categoryId: Int,
        $categoryIds: [Int!],
        $groupByStatus: Int,
        $countOnly: Boolean,
        $participationTypes: [Int!],
        $releaseDateTypes: [Int!],
        $authors: [Int!]
      ) {
        groupedEligibleArcsCount (
          needle: $needle,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          penNameId: $penNameId,
          hasComeViaLink: $hasComeViaLink,
          arcTeamId: $arcTeamId,
          activeOnly: $activeOnly,
          pastOnly: $pastOnly,
          arcType: $arcType,
          sites: $sites,
          minLength: $minLength,
          maxLength: $maxLength,
          audiences: $audiences,
          languages: $languages,
          canReviewInDays: $canReviewInDays,
          categoryId: $categoryId,
          categoryIds: $categoryIds,
          groupByStatus: $groupByStatus,
          countOnly: $countOnly,
          participationTypes: $participationTypes,
          releaseDateTypes: $releaseDateTypes,
          authors: $authors
        ) {
          authorTeamCount,
          allCount,
          savedCount
        }
      }
    `
  })
}

const getEligibleGrouped = (params?: ArcGetEligibleArgs): Promise<ArcEligibleGroupResultDto> => {
  return useApollo().query<ArcEligibleGroupResultDto>({
    variables: {
      ...params
    },
    query: gql`
      query GetGroupedEligibleArcs (
        $needle: String,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $penNameId: Int
        $hasComeViaLink: Boolean,
        $arcTeamId: Int,
        $activeOnly: Boolean,
        $pastOnly: Boolean,
        $arcType: Int,
        $sites: Int,
        $minLength: Int,
        $maxLength: Int,
        $audiences: [Int!],
        $languages: [Int!],
        $canReviewInDays: Int,
        $categoryId: Int,
        $categoryIds: [Int!],
        $groupByStatus: Int,
        $participationTypes: [Int!],
        $releaseDateTypes: [Int!],
        $authors: [Int!]
      ) {
        groupedEligibleArcs (
          needle: $needle,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          penNameId: $penNameId,
          hasComeViaLink: $hasComeViaLink,
          arcTeamId: $arcTeamId,
          activeOnly: $activeOnly,
          pastOnly: $pastOnly,
          arcType: $arcType,
          sites: $sites,
          minLength: $minLength,
          maxLength: $maxLength,
          audiences: $audiences,
          languages: $languages,
          canReviewInDays: $canReviewInDays,
          categoryId: $categoryId,
          categoryIds: $categoryIds,
          groupByStatus: $groupByStatus,
          participationTypes: $participationTypes,
          releaseDateTypes: $releaseDateTypes,
          authors: $authors
        ) {
          allCount,
          authorTeamCount,
          savedCount,
          result {
            totalRows,
            items {
              ...ArcFragment
              ...ArcScoreDebugFragment
            }
          }
        }
      }
      ${arcFragments.arc}
      ${arcFragments.arcScoreDebug}
    `
  }).then(result => {
    return {
      ...result,
      result: {
        totalRows: result.result.totalRows,
        items: result.result.items.map(getArcResult)
      }
    }
  })
}

const getEligible = (params?: ArcGetEligibleArgs): Promise<ArcPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetEligibleArcs (
        $needle: String,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $penNameId: Int
        $hasComeViaLink: Boolean,
        $arcTeamId: Int,
        $activeOnly: Boolean,
        $pastOnly: Boolean,
        $arcType: Int,
        $sites: Int,
        $minLength: Int,
        $maxLength: Int,
        $audiences: [Int!],
        $languages: [Int!],
        $canReviewInDays: Int,
        $categoryId: Int,
        $categoryIds: [Int!],
        $allUserOwned: Boolean,
        $excludeArcId: Int,
        $allowOwnClaim: Boolean,
        $penName: String,
        $participationTypes: [Int!],
        $releaseDateTypes: [Int!],
        $authors: [Int!]
      ) {
        eligibleArcs (
          needle: $needle,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          penNameId: $penNameId,
          hasComeViaLink: $hasComeViaLink,
          arcTeamId: $arcTeamId,
          activeOnly: $activeOnly,
          pastOnly: $pastOnly,
          arcType: $arcType,
          sites: $sites,
          minLength: $minLength,
          maxLength: $maxLength,
          audiences: $audiences,
          languages: $languages,
          canReviewInDays: $canReviewInDays,
          categoryId: $categoryId,
          categoryIds: $categoryIds,
          allUserOwned: $allUserOwned,
          excludeArcId: $excludeArcId,
          allowOwnClaim: $allowOwnClaim,
          penName: $penName,
          participationTypes: $participationTypes,
          releaseDateTypes: $releaseDateTypes,
          authors: $authors
        ) {
          totalRows,
          items {
            ...ArcFragment
          }
        }
      }
      ${arcFragments.arc}
    `
  }).then((result: ArcPagedResultDto) => {
    return {
      totalRows: result.totalRows,
      items: result.items.map(getArcResult)
    }
  })
}

const getLanguageFlags = (languages: number[] = []) => {
  return languages.reduce((combinedFlags, language) => {
    return combinedFlags | language
  }, 0)
}

const getLanguagesFromFlags = (flags: number = 0) => {
  const languagesWithFlags = [
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ENGLISH,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.SPANISH,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.GERMAN,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.FRENCH,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ITALIAN,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.PORTUGUESE,
    bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.OTHER
  ]

  return languagesWithFlags
    .filter(language => (flags & language) === language)
}

const resetFindArcFilters = (): FindArcFilters => {
  return {
    selectedArcType: -1,
    sites: bsConstants.ARCS.REVIEWS.ALL_FLAGS,
    reviewInDays: 0,
    minLength: bsConstants.BOOKS.WORD_COUNT_RANGE.MIN,
    maxLength: bsConstants.BOOKS.WORD_COUNT_RANGE.MAX,
    audienceTypes: [1,2,3,4],
    languages: [
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ENGLISH,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.SPANISH,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.GERMAN,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.FRENCH,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ITALIAN,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.PORTUGUESE,
      bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.OTHER
    ],
    searchValue: '',
    prependSearchValue: '',
    categoryId: -1,
    categoryIds: [],
    advancedFilterCall: false,
    appliedCount: 0,
    /**
     * "Recommended" is the default feed sort: the better first impression for readers who have never
     * picked a sort, and what logged out visitors get (sc-27838). A saved selection (findArcFilters,
     * local or on the account) is applied over these defaults, so anyone who has touched the filters
     * keeps the sort they had.
     *
     * dev made this change in `arc.service.ts`, which stage 6g split three ways; these defaults landed
     * here.
     */
    sortBy: bsConstants.ARCS.SORTING.RECOMMENDED,
    // The Saved tab has a sort of its own - New, Review due soon or Ending soon - and starts
    // on New (sc-28411). It leaves the feed's sort above, and its default, alone.
    savedSortBy: bsConstants.ARCS.SORTING.NEW,
    activeFiltersString: [],
    currentPage: 1,
    authors: [0],
    releaseDateTypes: [1,2],
    participationTypes: [1,2],
    // default all filters to expanded
    expandedFilters: ['ef:s', 'ef:c', 'ef:a', 'ef:lg', 'ef:f', 'ef:r', 'ef:av', 'ef:au', 'ef:w', 'ef:t'],
    displayResultsAs: 'list'
  }
}

const getFindArcFilterCount = (currentFilters: FindArcFilters & HasIndex): number => {
  const defaults: FindArcFilters & HasIndex = resetFindArcFilters()
  let appliedCount = 0

  // Loop tha active filters
  for (const key in currentFilters) {
    // Exclude some props we don't care about
    if (currentFilters.hasOwnProperty(key) && !['advancedFilterCall', 'appliedCount'].includes(key)) {
      // Check if the value matches (filter not set)
      let valueMatches = currentFilters[key] === defaults[key]
      // Or in the case of an array, that the lengths are the same
      if (Array.isArray(currentFilters[key])) {
        valueMatches = currentFilters[key].length === defaults[key].length
      }

      // If some values don't match, increment the counter.
      if (!valueMatches) {
        appliedCount++
      }
    }
  }

  return appliedCount
}

const shouldShowExplicitTag = (arc: ArcDto): boolean => {
  return arc.book?.audience === bsConstants.BOOKS.AUDIENCE_TYPE.EXPLICIT ||
    arc.book?.audience === bsConstants.BOOKS.AUDIENCE_TYPE.MATURE
}

const sizeAllowsEmail = (arc: ArcDto): boolean => {
  return canEmail(bsConstants.ARCS.CLAIMS.FILE_TYPE.EPUB, arc) ||
    canEmail(bsConstants.ARCS.CLAIMS.FILE_TYPE.PDF, arc)
}

const canEmail = (fileType: number, arc: ArcDto): boolean => {
  let size: number | undefined = void 0
  switch (fileType) {
    case bsConstants.ARCS.CLAIMS.FILE_TYPE.EPUB:
      size = arc.book?.epubFileSizeBytes
      break
    case bsConstants.ARCS.CLAIMS.FILE_TYPE.PDF:
      size = arc.book?.pdfFileSizeBytes
      break
  }

  return !!size && size < maxEmailFileSize
}

const getOptionalReviewCount = (arc: ArcDto): number => {
  const requiredSiteCount = countSiteFlags(arc.reviewFlags)
  return arc.requiredReviewCount - requiredSiteCount
}

const getAuthorReviewsLinkForSite = (site: number, book: BookDto): string => {
  const { ...sites } = bsConstants.ARCS.REVIEWS.FLAGS

  const arcSite = getArcSitesFromFlags(site)[0]
  const link = (book as HasIndex)[arcSite.fieldName]

  switch (site) {
    case sites.BARNES:
      return link + '#reviews'
    case sites.KOBO:
      return link + '#ratings-and-reviews'
    case sites.GOOGLE:
      return link + '&showAllReviews=true'
    case sites.AUDIBLE_US:
      return link + '#customer-reviews'
    case sites.AUDIBLE_UK:
      return link + '#customer-reviews'
    default:
      return link
  }
}

const getReviewLinkForSite = (
  site: number,
  arc: ArcDto,
  arcClaim?: ArcClaimDto // only needed for Audible
): string | undefined => {
  if (!arc.book) return

  const { ...sites } = bsConstants.ARCS.REVIEWS.FLAGS
  switch (site) {
    case sites.AMAZON:
      if (validStr(arc.book.linkAmazonAsinPrint)) {
        return `https://www.amazon.com/gp/product/${arc.book.linkAmazonAsinPrint}`
      }
      
      return validStr(arc.book.linkAmazonAsin)
        ? `https://www.amazon.com/gp/product/${arc.book.linkAmazonAsin}`
        : ''
    case sites.BARNES:
    case sites.AUDIO_BARNES: {
      const link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkBarnes : arc.book.linkBarnesAudio
      if (!link) {
        return `https://www.barnesandnoble.com/s/${arc.book.title}`
      }
      // Barnes & Noble retired the dedicated /reviews/ pages, so the old
      // /w/ -> /reviews/ rewrite (plus #reviews) now 404s. Return the
      // author-supplied product link as-is.
      return link
    }
    case sites.KOBO:
    case sites.AUDIO_KOBO: {
      let link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkKobo : arc.book.linkKoboAudio
      if (!link) {
        return `https://www.kobo.com/us/en/search?query=${arc.book.title}`
      }
      // 1. Add #ratings-and-reviews to the end
      // 2. Remove country and language identifiers. In this case, that's "th/en/" (Thailand/English) just after the URL.
      // Removing this will send the user to Kobo for their own country in their own language.

      // 1
      link = link + '#ratings-and-reviews'

      // 2
      const regex = /www.kobo.com(.*\/)ebook/gi
      const match = regex.exec(link)
      if (match)
        return link.replace(match[1], '/')
      else
        return link
    }
    case sites.ITUNES:
    case sites.AUDIO_ITUNES: {
      let link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkITunes : arc.book.linkITunesAudio
      if (!link) {
        return `https://www.apple.com/us/search/${arc.book.title}`
      }
      // 1. Remove existing query params
      // 2. Add our own affiliate link
      // 3. Send them to the reviews section of the book

      // 1
      link = link.substring(0, link.includes('?') ? link.lastIndexOf('?') : link.length)

      // 2
      link = link + '?at=1001l9EK'

      // 3
      //link = link + '#see-all/reviews'
      return link
    }
    case sites.AUDIBLE_US:
      return arc.book.linkAudibleUs
        ? arc.book.linkAudibleUs + '#customer-reviews'
        : void 0
    case sites.AUDIBLE_UK:
      return arc.book.linkAudibleUk
        ? arc.book.linkAudibleUk + '#customer-reviews'
        : void 0
    case sites.AUDIBLE:
      // special case here
      // users will see only Audible site,
      // but when they claim arc, it will be either US or UK
      // hence why we need to check claim here

      if (arcClaim?.selectedAudioSiteFlag === bsConstants.ARCS.REVIEWS.FLAGS.AUDIBLE_US && arc.book.linkAudibleUs) {
        return arc.book.linkAudibleUs + '#customer-reviews'
      } else if (arcClaim?.selectedAudioSiteFlag === bsConstants.ARCS.REVIEWS.FLAGS.AUDIBLE_UK && arc.book.linkAudibleUk) {
        return arc.book.linkAudibleUk + '#customer-reviews'
      }

      return void 0
    // These are just returning normal links the author supplies
    case sites.GOOD_READS:
    case sites.AUDIO_GOODREADS: {
      const link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkGoodreads : arc.book.linkGoodreadsAudio
      if (!link) {
        return `https://www.goodreads.com/search?q=${arc.book.title}`
      }

      return link
    }
    case sites.BOOK_BUB:
    case sites.AUDIO_BOOK_BUB: {
      const link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkBookbub : arc.book.linkBookbubAudio
      if (!link) {
        return `https://www.bookbub.com/search?search=${arc.book.title}`
      }

      return link
    }
    case sites.GOOGLE:
    case sites.AUDIO_GOOGLE: {
      const link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkGoogle : arc.book.linkGoogleAudio
      if (!link) {
        return `https://play.google.com/store/search?q=${arc.book.title}&c=books`
      }

      return link
    }
    case sites.SMASH_WORDS:
      if (!arc.book.linkSmashwords) {
        return `https://www.smashwords.com/books/titles/${arc.book.title}`
      }

      return arc.book.linkSmashwords
    case sites.STORY_GRAPH:
    case sites.AUDIO_STORY_GRAPH: {
      const link = arc.arcType === bsConstants.ARCS.TYPE.EBOOK ? arc.book.linkStoryGraph : arc.book.linkStoryGraphAudio
      // The review form is only linkable by book id, so extract it from the author's
      // /books/<uuid> link. StoryGraph still requires the book to be marked as read first.
      const bookId = link?.match(/\/books\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)?.[1]
      if (bookId) {
        return `https://app.thestorygraph.com/reviews/new?book_id=${bookId}`
      }

      return `https://app.thestorygraph.com/browse?search_term=${arc.book.title}`
    }
    default:
      return ''
  }
}

const getWebStatsSelectionSet = (fields: ArcWebStatsField[]): string => {
  return Array.from(new Set(fields)).map(field => {
    switch (field) {
      case 'topArc':
        return `
          topArc {
            id,
            totalClaims,
            daysLeft,
            book {
              title,
              bookCover,
              penName,
              authorPenName {
                name
              }
            }
          }
        `
      default:
        return field
    }
  }).join('\n')
}

const getWebStats = (fields: ArcWebStatsField[] = defaultWebStatsFields): Promise<ArcWebStatsDto> => {
  const selectionSet = getWebStatsSelectionSet(fields)

  return useApollo().query({
    query: gql`
      query GetArcWebStats {
        arcWebStats {
          ${selectionSet}
        }
      }
    `
  })
}

const getUserStats = (): Promise<ArcWebStatsDto> => {
  return useApollo().query({
    query: gql`
      query UserArcStats {
        userArcStats {
          arcReviewCount,
          arcReviewUserCount,
          arcLiveCount,
          authorCount,
          totalArcCount,
          scheduledArcCount
        }
      }
    `
  })
}

const arcAvailableDownloadCodeCounts = (id: number): Promise<AvailableDownloadCodeDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query ArcAvailableDownloadCodeCounts ( $id: Int!) {
        arcAvailableDownloadCodeCounts (id: $id) {
          site,
          count,
          total
        }
      }
    `
  })
}

const getUsedDownloadCodes = (id: number): Promise<UsedDownloadCodeDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query ArcUsedDownloadCodes ( $id: Int!) {
        arcUsedDownloadCodes (id: $id) {
          code
        }
      }
    `
  })
}

const claimAppleBooksCode = (claimId: number): Promise<string> => {
  return useApollo().mutate({
    variables: {
      id: claimId
    },
    mutation: gql`
      mutation ClaimAppleBooksCode ($id: Int!) {
        claimAppleBooksCode (id: $id)
      }
    `
  })
}

const getActiveArcDates = (args: GetActiveArcDatesArgs): Promise<KvpDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetActiveArcDates (
        $excludeArcId: Int
      ) {
        getActiveArcDates (
          excludeArcId: $excludeArcId
        ) {
          key,
          value
        }
      }
    `
  })
}

const getArcFollowerStats = (id: number): Promise<ArcFollowerStatsDto> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query GetArcFollowerStats (
        $id: Int!
      ) {
        getArcFollowerStats (
          id: $id
        ) {
          penNameFollowerCount,
          arcTeamFollowerCount,
          arcTeamMemberCount
        }
      }
    `
  })
}

const getArcTotalOverdueDays = (): Promise<number> => {
  return useApollo().query({
    query: gql`
      query GetArcTotalOverdueDays {
        getArcTotalOverdueDays
      }
    `
  })
}

const hasAlreadyCounted = async (id: number, property: string) => {
  const existingProps = JSON.parse(LocalStorage.getItem<string>('view_tracker') + '')
  if (!existingProps) {
    return false
  }

  // if the user has already been counted within the last hour, don't count again
  const lastCounted = existingProps[id] && existingProps[id][property] ? existingProps[id][property] : void 0
  if (lastCounted && lastCounted > ((Date.now() / 1000) - 3600)) {
    return true
  }

  return false
}

const trackUserCounted = async (id: number, property: string) => {
  let existingProps = JSON.parse(LocalStorage.getItem<string>('view_tracker') + '')
  if (!existingProps) {
    existingProps = {
      [id]: {
        [property]: Date.now() / 1000
      }
    }
  } else if (!existingProps[id]) {
    existingProps[id] = {
      [property]: Date.now() / 1000
    }
  } else {
    existingProps[id][property] = Date.now() / 1000
  }

  LocalStorage.set('view_tracker', JSON.stringify(existingProps))
}

const addViewToArc = async (id: number): Promise<any> => {
  /*if (await hasAlreadyCounted(id, 'vc')) {
    return
  }*/

  await useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation AddViewToArc (
        $id: Int!
      ) {
        addViewToArc(id: $id) {
          id
        }
      }
    `
  })
  // await trackUserCounted(id, 'vc')
}

const addImpressionToArc = async (id: number): Promise<any> => {
  /*if (await hasAlreadyCounted(id, 'ic')) {
    return
  }*/

  await useApollo().mutate({
    variables: {

      id
    },
    mutation: gql`
      mutation AddImpressionToArc (
        $id: Int!
      ) {
        addImpressionToArc(id: $id) {
          id
        }
      }
    `
  })
  // await trackUserCounted(id, 'ic')
}

const crud = useBaseCrud<ArcDto>('arc')

/**
 * Chained the same way as the other two, so it returns this bag.
 */
const service = {
  ...crud,

  addReview,
  translateReview,
  deleteReview,
  addSiteReview,
  reportPirate,
  getReportedPirateCount,
  getArcSites,
  getArcSitesFromFlags,
  getArcSitesFromFlagsExcludingCountrySpecificAudible,
  isValidReviewLink,
  countSiteFlags,
  getEligibleGroupedCount,
  getEligibleGrouped,
  getEligible,
  getLanguageFlags,
  getLanguagesFromFlags,
  resetFindArcFilters,
  getFindArcFilterCount,
  shouldShowExplicitTag,
  sizeAllowsEmail,
  canEmail,
  getOptionalReviewCount,
  getAuthorReviewsLinkForSite,
  getReviewLinkForSite,
  getWebStats,
  getUserStats,
  arcAvailableDownloadCodeCounts,
  getUsedDownloadCodes,
  claimAppleBooksCode,
  getActiveArcDates,
  getArcFollowerStats,
  getArcTotalOverdueDays,
  hasAlreadyCounted,
  trackUserCounted,
  addViewToArc,
  addImpressionToArc,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function arcReviewStatsService () {
  return service
}
